import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import { FilterMatchMode } from 'primereact/api';
import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Card } from 'primereact/card';
import { Messages } from 'primereact/messages';
import { Toolbar } from 'primereact/toolbar';
import { InputText } from 'primereact/inputtext';
import { useAuth } from '../contexts/AuthContext';
import Container from 'react-bootstrap/Container';
import { Toast } from 'primereact/toast';
import useStatusPageScript from '../hooks/useStatusPageScript.js';
import { Dialog } from 'primereact/dialog';
import { ScrollTop } from 'primereact/scrolltop';
import { OverlayPanel } from 'primereact/overlaypanel';
import { ProgressBar } from 'primereact/progressbar';
import { Checkbox } from 'primereact/checkbox';
import walletApi from '../api/walletApi';
import '../config.js';
import AccessState from './AccessState';
import usePageAccessRedirect from '../hooks/usePageAccessRedirect';

const Locations = () => {
  // Load the status page script
  useStatusPageScript();
  
  const { customerId, customerName } = useParams();
  const [locations, setLocations] = useState([]);
  const navigate = useNavigate();
  const [rowSelectionModel, setRowSelectionModel] = useState();
  const [filters, setFilters] = useState({
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    name: { value: null, matchMode: FilterMatchMode.STARTS_WITH },
    extra: { value: null, matchMode: FilterMatchMode.STARTS_WITH },
  });
  const [globalFilterValue, setGlobalFilterValue] = useState('');
  const [loading, setLoading] = useState(true);
  const msgs = useRef(null);
  const [createVisible, setCreateVisible] = useState(false);
  const [newLocationName, setNewLocationName] = useState('');
  const [newLocationExtra, setNewLocationExtra] = useState('');
  const [keypadCodes, setKeypadCodes] = useState([]);
  const toast = useRef(null);
  const toastBC = useRef(null);
  const dt = useRef(null);
  const { accessIssue, clearAccessIssue, handleAccessError } = usePageAccessRedirect({
    deniedTitle: 'Locations Access Denied',
    deniedMessage: 'You do not have permission to view or manage locations for this customer.',
  });

  const [editVisible, setEditVisible] = useState(false);
  const [editSaving, setEditSaving] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [editForm, setEditForm] = useState({
    id: 0,
    name: '',
    extra: '',
    gympassVenueId: '',
    walletPassesEnabled: false,
    walletPassPortalCode: '',
  });
  const editMsgs = useRef(null);

  const op = useRef(null);

  useEffect(() => {
    const fetchLocations = async () => {
      getLocations();
    };

    fetchLocations();

    //get filters from local storage
    let _filters = JSON.parse(localStorage.getItem('locationsFilters'));
    if (_filters) {
      setFilters(_filters);
      setGlobalFilterValue(_filters.global.value);
    }
  }, [customerId]);

  const getLocations = async () => {
    try {
      clearAccessIssue();
      setLoading(true);
      setLocations([]);
      const token = localStorage.getItem('token');
      const response = await axios.get(global.config.url + `/locations/ByCustomer/${customerId}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
      setLocations(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching locations:', error);
      if (handleAccessError(error)) {
        setLoading(false);
      } else {
        // Show error message
        if (msgs.current) {
          msgs.current.show({severity: 'error', summary: '', detail: 'Error fetching locations, please try again.'});
        }
      }

    }
  };

  const getClients = () => {
    if(rowSelectionModel === undefined || rowSelectionModel.id === 0 || rowSelectionModel.name === null){
      msgs.current.show({severity: 'error', summary: '', detail: 'Please select a location to view its clients'});
    }
    else{
      navigate(`/clients/Location/${rowSelectionModel.name}/${rowSelectionModel.id}`);
    }
  };

  const getAccessPoints = () => {
  
    if(rowSelectionModel === undefined || rowSelectionModel.id === 0 || rowSelectionModel.name === null){
      msgs.current.show({severity: 'error', summary: '', detail: 'Please select a location to view its access points.'});
    }
    else{
      navigate(`/accesspoints/Location/${rowSelectionModel.name}/${rowSelectionModel.id}`);
    }
  };

  const getReplacementTiles = () => {
  
    if(rowSelectionModel === undefined || rowSelectionModel.id === 0 || rowSelectionModel.name === null){
      msgs.current.show({severity: 'error', summary: '', detail: 'Please select a location to view its replacement tiles.'});
    }
    else{
      navigate(`/replacementtiles/${rowSelectionModel.name}/${rowSelectionModel.id}`);
    }
  };

    const getHubRelays = () => {
    if(rowSelectionModel === undefined || rowSelectionModel.id === 0 || rowSelectionModel.gate === null){
      msgs.current.show({severity: 'error', summary: '', detail: 'Please select an access point to view hub relays.'});
    }
    else{
      navigate(`/hubrelay/Location/${rowSelectionModel.id}`);
    }
  }

  const getLogs = () => {
  
    if(rowSelectionModel === undefined || rowSelectionModel.id === 0 || rowSelectionModel.name === null){
      msgs.current.show({severity: 'error', summary: '', detail: 'Please select a location to view its logs'});
    }
    else{
      navigate(`/logs/Location/${rowSelectionModel.name}/${rowSelectionModel.id}`);
    }
  };

  const exportCSV = (selectionOnly) => {
    dt.current.exportCSV({ selectionOnly });
  };

  const onGlobalFilterChange = (e) => {
    const value = e.target.value;
    let _filters = { ...filters };

    _filters['global'].value = value;

    //save _filters to local storage
    localStorage.setItem('locationsFilters', JSON.stringify(_filters));

    setFilters(_filters);
    setGlobalFilterValue(value);
  };

const showKeyPadCodes = () => {
  
  if(rowSelectionModel === undefined || rowSelectionModel.id === 0 || rowSelectionModel.name === null){
    msgs.current.show({severity: 'error', summary: '', detail: 'Please select a location to view its keypad codes.'});
  }
  else{
    navigate(`/keypadcodes/${rowSelectionModel.id}`);
  }
};

const showReceptionCodes = () => {
  
  if(rowSelectionModel === undefined || rowSelectionModel.id === 0 || rowSelectionModel.name === null){
    msgs.current.show({severity: 'error', summary: '', detail: 'Please select a location to view its reception codes.'});
  }
  else{
    navigate(`/receptioncodes/${rowSelectionModel.id}`);
  }
};

const showOpenGateCodes = () => {
  
  if(rowSelectionModel === undefined || rowSelectionModel.id === 0 || rowSelectionModel.name === null){
    msgs.current.show({severity: 'error', summary: '', detail: 'Please select a location to view its OpenGate codes.'});
  }
  else{
    navigate(`/opengatecodes/${rowSelectionModel.id}`);
  }
};

const clearFilter = () => {
  
  let _filters = { ...filters };
  _filters['global'].value = null;
  setFilters(_filters);
  setGlobalFilterValue('');
  localStorage.removeItem('locationsFilters');
};

const markLocationAsLive = () => {
  
  if(rowSelectionModel === undefined || rowSelectionModel.id === 0 || rowSelectionModel.name === null){
    msgs.current.show({severity: 'error', summary: '', detail: 'Please select a location to mark as live.'});
  }
  else{
    let conf = window.confirm(`Are you sure you want to mark location ${rowSelectionModel.name} as live?`);
    if(!conf){
      return;
    }
    let token = localStorage.getItem('token');
    let axiosConfig = {
      headers: {
        Authorization: `Bearer ${token}`,
      }
    };
    axios.get(global.config.url + `/locations/marklocationaslive/${rowSelectionModel.id}`, axiosConfig
    ).then((response) => {
      if(response.status === 200){
        msgs.current.show({severity: 'success', summary: '', detail: `Location ${rowSelectionModel.name} marked as live successfully.`});
      }
      else{
        msgs.current.show({severity: 'error', summary: '', detail: `Error marking location as live, please try again.`});
      }
    }).catch((error) => {
      console.error('Error marking location as live:', error);
      if (handleAccessError(error, { deniedMessage: 'You do not have permission to update this location.' })) {
        return;
      } else {
        msgs.current.show({severity: 'error', summary: '', detail: `Error marking location as live, please try again.`});
      }
    });
  }
};

const getGroups = () => {
  if(rowSelectionModel === undefined || rowSelectionModel.id === 0 || rowSelectionModel.name === null){
    msgs.current.show({severity: 'error', summary: '', detail: 'Please select a location to view its Groups.'});
  }
  else{
    navigate(`/groups/${rowSelectionModel.id}`);
  }
};

  const startContent = (
    <React.Fragment>
      <div>
        <Button onClick={() => getClients()} label="Get Clients"  icon="pi pi-desktop" className="ml-3 mb-3" tooltip="Get clients for this location" tooltipOptions={{position:"bottom"}}/>
        <Button onClick={() => getAccessPoints()} label="Get Access Points" icon="pi pi-qrcode" className="p-button-success ml-3 mb-3" tooltip="Get access points for this location" tooltipOptions={{position:"bottom"}}/>
        <Button onClick={() => getLogs()} label="Get Access Logs" icon="pi pi-file" className="p-button-warning ml-3 mb-3" tooltip="Get access logs for this location" tooltipOptions={{position:"bottom"}}/>
        <Button onClick={() => sendQRCodes()} label="Send QR Codes" icon="pi pi-qrcode" className="p-button-help ml-3 mb-3" tooltip="Send QR codes for this location" tooltipOptions={{position:"bottom"}}/>
      </div>
      <div className=''>
        <Button onClick={() => removeStaffCode()} label="Remove Staff Code" icon="pi pi-users" className="p-button-danger ml-3 mb-3" tooltip="Remove staff code for this location" tooltipOptions={{position:"bottom"}}/>
        <Button onClick={() => showKeyPadCodes()} label="Show Keypad Codes" icon="pi pi-codes" className="p-button-info ml-3 mb-3" tooltip="Show keypad codes for this location" tooltipOptions={{position:"bottom"}}/>
        <Button onClick={() => showReceptionCodes()} label="Show Reception Codes" icon="pi pi-codes" className="p-button-primary ml-3 mb-3" tooltip="Show reception codes for this location" tooltipOptions={{position:"bottom"}}/>
        <Button onClick={() => showOpenGateCodes()} label="Show OpenGate Codes" icon="pi pi-codes" className="p-button-secondary ml-3 mb-3" tooltip="Show open gate codes for this location" tooltipOptions={{position:"bottom"}}/>
      </div>
      <div className=''>
        <Button onClick={() => markLocationAsLive()} label="Mark as Live" icon="pi pi-check" className="p-button-success ml-3 mb-3" tooltip="Mark this location as live" tooltipOptions={{position:"bottom"}}/>
        <Button onClick={() => getReplacementTiles()} label="Get Replacement Tiles" icon="pi pi-clone" className="p-button-help ml-3 mb-3" tooltip="Get replacement tiles for this location" tooltipOptions={{position:"bottom"}}/>
        <Button onClick={() => getHubRelays()} label="Get Hub Relays" icon="pi pi-sitemap" className="p-button-warning ml-3 mb-3" tooltip="Get hub relays for this location" tooltipOptions={{position:"bottom"}}/>
        <Button onClick={() => getGroups()} label="Get Groups" icon="pi pi-users" className="p-button-info ml-3 mb-3" tooltip="Get groups for this location" tooltipOptions={{position:"bottom"}}/>
      </div>
      <div>
        <Button label="Get Staff Members" icon="pi pi-id-card" className="p-button-secondary ml-3 mb-3" tooltip="Get staff members for this location" tooltipOptions={{position:"bottom"}} onClick={() => navigate(`/staffmembers/Location/${rowSelectionModel ? rowSelectionModel.id : 0}`)}/>
      </div>
    </React.Fragment>
);

const footerContent = (
  <div>
      <Button label="No" icon="pi pi-times" onClick={() => setCreateVisible(false)} className="p-button-text" />
      <Button label="Yes" icon="pi pi-check" onClick={() => confirm(true)} autoFocus />
  </div>
);

const removeStaffCode = async () => {
  let token = localStorage.getItem('token');
  let axiosConfig = {
    headers: {
      Authorization: `Bearer ${token}`,
    }
  };
  try {
    const response = await axios.delete(global.config.url + `/locations/RemoveStaffKeypadCode/${rowSelectionModel.id}`, axiosConfig);
    if(response.status === 200){
      if(response.data === true){
        msgs.current.show({severity: 'success', summary: '', detail: `Staff code removed successfully.`});
      }
      else{
        msgs.current.show({severity: 'error', summary: '', detail: `Error removing staff code, please try again.`});
      }
    }
    else{
      msgs.current.show({severity: 'error', summary: '', detail: `Error removing staff code, please try again.`});
    }
  } catch (error) {
    console.error('Error fetching customers:', error);
    if (handleAccessError(error, { deniedMessage: 'You do not have permission to update staff codes for this location.' })) {
      return;
    } else {
      msgs.current.show({severity: 'error', summary: '', detail: `Error removing staff code, please try again.`});
    }
  }
};


const sendQRCodes = async () => {
  let token = localStorage.getItem('token');
  let axiosConfig = {
    headers: {
      Authorization: `Bearer ${token}`,
    }
  };
  try {
    const response = await axios.get(global.config.url + `/locations/qr/${rowSelectionModel.id}`, axiosConfig);
    if(response.status === 200){
      if(response.data === true){
        msgs.current.show({severity: 'success', summary: '', detail: `QR codes sent successfully.`});
      }
      else{
        msgs.current.show({severity: 'error', summary: '', detail: `Error sending QR codes, check if the links have been submitted and try again.`});
      }
    }
    else{
      msgs.current.show({severity: 'error', summary: '', detail: `Error sending QR codes, please try again.`});
    }
  } catch (error) {
    console.error('Error fetching customers:', error);
    if (handleAccessError(error, { deniedMessage: 'You do not have permission to send QR codes for this location.' })) {
      return;
    } else {
      msgs.current.show({severity: 'error', summary: '', detail: `Error sending QR codes, please try again.`});
    }
  }
};


const clear = () => {
  
  toastBC.current.clear();
};

const locationPayload = (location, changes = {}) => ({
  id: location.id,
  name: location.name,
  customerId: location.customerId,
  lms: location.lms,
  extra: location.extra || '',
  status: location.status,
  gympassVenueId: location.gympassVenueId || '',
  walletPassesEnabled: Boolean(location.walletPassesEnabled),
  ...changes,
});

const updateLocation = async (newData) => {
  clear(true);
  const token = localStorage.getItem('token');
  let axiosConfig = {
    headers: {
      Authorization: `Bearer ${token}`,
    }
  };
  try {
    const response = await axios.put(global.config.url + `/locations/${newData.id}`, newData, axiosConfig);
    if(response.status === 204){
      msgs.current.show({severity: 'success', summary: '', detail: `Location ${newData.name} successfully updated.`});
      getLocations();
      setEditVisible(false);
    }
    else{
      msgs.current.show({severity: 'error', summary: '', detail: 'Error updating location, please try again.'});
    }
  } catch (error) {
    console.error('Error fetching customers:', error);
    if (handleAccessError(error, { deniedMessage: 'You do not have permission to update this location.' })) {
      return;
    } else {
      msgs.current.show({severity: 'error', summary: '', detail: 'Error updating location, please try again.'});
    }
  }
};

  const openEditDialog = (rowData) => {
    setEditTarget(rowData);
    setEditForm({
      id: rowData && rowData.id ? rowData.id : 0,
      name: rowData && rowData.name ? rowData.name : '',
      extra: rowData && rowData.extra ? rowData.extra : '',
      gympassVenueId: rowData && rowData.gympassVenueId ? rowData.gympassVenueId : '',
      walletPassesEnabled: Boolean(rowData && rowData.walletPassesEnabled),
      walletPassPortalCode: rowData && rowData.walletPassPortalCode ? rowData.walletPassPortalCode : '',
    });
    if (editMsgs.current) editMsgs.current.clear();
    setEditVisible(true);
  };

  const closeEditDialog = () => {
    setEditVisible(false);
    setEditSaving(false);
    setEditTarget(null);
    if (editMsgs.current) editMsgs.current.clear();
  };

  const saveEditDialog = async () => {
    const name = String(editForm.name || '').trim();
    if (!name) {
      if (editMsgs.current) {
        editMsgs.current.show({ severity: 'error', summary: '', detail: 'Name is required.' });
      }
      return;
    }

    if (!editTarget) {
      if (editMsgs.current) {
        editMsgs.current.show({ severity: 'error', summary: '', detail: 'No location selected.' });
      }
      return;
    }

    try {
      setEditSaving(true);
      if (editMsgs.current) editMsgs.current.clear();

      const payload = locationPayload(editTarget, {
        name,
        extra: String(editForm.extra || ''),
        gympassVenueId: String(editForm.gympassVenueId || ''),
        walletPassesEnabled: Boolean(editForm.walletPassesEnabled),
      });

      await updateLocation(payload);
      const portalCode = String(editForm.walletPassPortalCode || '').trim();
      if (portalCode && portalCode !== String(editTarget.walletPassPortalCode || '')) {
        await walletApi.saveLocationPortalCode(editTarget.id, portalCode);
      }
    } catch (error) {
      const detail =
        (error && error.response && error.response.data) ||
        (error && error.message) ||
        'Failed to update location.';
      if (editMsgs.current) {
        editMsgs.current.show({ severity: 'error', summary: '', detail });
      }
    } finally {
      setEditSaving(false);
    }
  };

const confirm = (create, newData) => {
  if(create === null){
    if(newData.name === undefined || newData.name === ''){
      msgs.current.show({severity: 'error', summary: '', detail: 'Please enter a name for the location.'});
      getLocations();
      return;
    }
    if(newData.extra === undefined){
      newData.extra = '';
    }
    toastBC.current.show({
      severity: 'info',
      sticky: true,
      className: 'border-none',
      content: (
          <div className="flex flex-column align-items-center" style={{ flex: '1' }}>
              <div className="text-center">
                  <i className="pi pi-exclamation-triangle" style={{ fontSize: '3rem' }}></i>
                  <div className="font-bold text-xl my-3">Are you sure?</div>
              </div>
              <div className="flex align-items-center">
                  <Button onClick={(e) => updateLocation(newData)} type="button" label="Confirm" className="p-button-success w-6rem" />
                  <Button onClick={(e) => clear(false)} type="button" label="Cancel" className="p-button-warning w-6rem mx-4" />
              </div>
          </div>
      )
    });
  }
  else{
    if(create){
      if(newLocationName=== undefined || newLocationName=== ''){
        toast.current.show({severity: 'error', summary: '', detail: 'Please enter a name for the new location.'});
        return;
      }
      toastBC.current.show({
        severity: 'info',
        sticky: true,
        className: 'border-none',
        content: (
            <div className="flex flex-column align-items-center" style={{ flex: '1' }}>
                <div className="text-center">
                    <i className="pi pi-exclamation-triangle" style={{ fontSize: '3rem' }}></i>
                    <div className="font-bold text-xl my-3">Are you sure?</div>
                </div>
                <div className="flex align-items-center">
                    <Button onClick={(e) => createLocation()} type="button" label="Confirm" className="p-button-success w-6rem" />
                    <Button onClick={(e) => clear(false)} type="button" label="Cancel" className="p-button-warning w-6rem mx-4" />
                </div>
            </div>
        )
      });
    }
    else{
      toastBC.current.show({
        severity: 'info',
        sticky: true,
        className: 'border-none',
        content: (
            <div className="flex flex-column align-items-center" style={{ flex: '1' }}>
                <div className="text-center">
                    <i className="pi pi-exclamation-triangle" style={{ fontSize: '3rem' }}></i>
                    <div className="font-bold text-xl my-3">Are you sure?</div>
                </div>
                <div className="flex align-items-center">
                    <Button onClick={(e) => deleteLocation()} type="button" label="Confirm" className="p-button-success w-6rem" />
                    <Button onClick={(e) => clear(false)} type="button" label="Cancel" className="p-button-warning w-6rem mx-4" />
                </div>
            </div>
        )
      });
    }
  }

};

const createLocation = async () => {
  clear(true);
  const token = localStorage.getItem('token');
  var newLocation = {
    name: newLocationName,
    extra: newLocationExtra,
    id: 0,
    lms: 0,
    customerId: customerId,
    status: true,
  }
  let axiosConfig = {
    headers: {
        'Content-Type': 'application/json;charset=UTF-8',
        "Access-Control-Allow-Origin": "*",
        Authorization: `Bearer ${token}`,
    }
  };
  try {

    const response = await axios.post(global.config.url + '/locations', newLocation, axiosConfig);
    console.log(response);
    if(response.status === 201){
      msgs.current.show({severity: 'success', summary: '', detail: `Location ${response.data.name} successfully created.`});
      getLocations();
      setCreateVisible(false);
      setNewLocationName('');
      setNewLocationExtra('');
    }
    else{
      toast.current.show({severity: 'error', summary: '', detail: 'Error creating location, please try again.'});
    }

  } catch (error) {
    console.error('Error fetching customers:', error);
    if (handleAccessError(error, { deniedMessage: 'You do not have permission to create locations for this customer.' })) {
      return;
    }
    else if(error.response && error.response.status === 400){
      toast.current.show({severity: 'error', summary: '', detail: 'Error creating location, please try again.'});
    }
    else if(error.response && error.response.status === 500){
      toast.current.show({severity: 'error', summary: '', detail: 'Error creating location, please try again.'});
    }
    else if(error.response && error.response.status === 409){
      toast.current.show({severity: 'error', summary: '', detail: error.response.data});
    }
  }

};

const deleteLocation = async () => {
  clear(true);
  const token = localStorage.getItem('token');
  if(rowSelectionModel === undefined || rowSelectionModel === 0){
    msgs.current.show({severity: 'error', summary: '', detail: 'Please select a location to delete.'});
    return;
  }
  try {
    let axiosConfig = {
      headers: {
          'Content-Type': 'application/json;charset=UTF-8',
          "Access-Control-Allow-Origin": "*",
          Authorization: `Bearer ${token}`,
      }
    };
    const response = await axios.delete(global.config.url + `/locations/${rowSelectionModel}`, axiosConfig);
    console.log(response);
    if(response.status === 204){
      msgs.current.show({severity: 'success', summary: '', detail: `Location deleted successfully.`});
      getLocations();
      setCreateVisible(false);
    }
    else{
      toast.current.show({severity: 'error', summary: '', detail: 'Error deleting location, please try again.'});
    }

  } catch (error) {
    console.error('Error fetching customers:', error);
    if (handleAccessError(error, { deniedMessage: 'You do not have permission to delete this location.' })) {
      return;
    }
    else if(error.response && error.response.status === 400){
      toast.current.show({severity: 'error', summary: '', detail: 'Error deleting location, please try again.'});
    }
    else if(error.response && error.response.status === 500){
      toast.current.show({severity: 'error', summary: '', detail: 'Error deleting location, please try again.'});
    }
  }
  
};

  const optionsSelected = (e, rowData) =>{
    op.current.toggle(e);
    setRowSelectionModel(rowData);
  }

  const locationOptiosTemplate = (rowData) => {
    return <Button link icon="pi pi-ellipsis-v" onClick={(e) => optionsSelected(e, rowData)} />;
  };

  const editActionTemplate = (rowData) => {
    return (
      <Button
        link
        icon="pi pi-pencil"
        onClick={() => openEditDialog(rowData)}
        tooltip="Edit location"
        tooltipOptions={{ position: 'bottom' }}
      />
    );
  };

  const walletPassesTemplate = (rowData) => {
    const isEnabled = Boolean(rowData.walletPassesEnabled);
    return (
      <Checkbox
        inputId={`wallet-passes-${rowData.id}`}
        checked={isEnabled}
        onChange={async (event) => {
          const walletPassesEnabled = Boolean(event.checked);
          const originalLocation = rowData;
          setLocations((current) => current.map((location) => location.id === rowData.id ? { ...location, walletPassesEnabled } : location));
          try {
            const token = localStorage.getItem('token');
            await axios.put(global.config.url + `/locations/${rowData.id}`, locationPayload(originalLocation, { walletPassesEnabled }), {
              headers: { Authorization: `Bearer ${token}` },
            });
          } catch (error) {
            setLocations((current) => current.map((location) => location.id === rowData.id ? originalLocation : location));
            if (!handleAccessError(error, { deniedMessage: 'You do not have permission to update this location.' }) && msgs.current) {
              msgs.current.show({ severity: 'error', summary: '', detail: 'Failed to update Wallet Passes for this location. The change was reverted.' });
            }
          }
        }}
        aria-label={`Enable Wallet Passes for ${rowData.name}`}
      />
    );
  };

  const enabledWalletPassesCount = locations.filter((location) => Boolean(location.walletPassesEnabled)).length;
  const areWalletPassesEnabledForAll = locations.length > 0 && enabledWalletPassesCount === locations.length;
  const areWalletPassesEnabledForSome = enabledWalletPassesCount > 0 && !areWalletPassesEnabledForAll;

  const updateWalletPassesForAllLocations = async (walletPassesEnabled) => {
    const targetLocations = locations.filter((location) => Boolean(location.walletPassesEnabled) !== walletPassesEnabled);
    if (targetLocations.length === 0) {
      msgs.current.show({ severity: 'info', summary: '', detail: `Wallet Passes are already ${walletPassesEnabled ? 'enabled' : 'disabled'} for all locations.` });
      return;
    }

    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const results = await Promise.all(targetLocations.map(async (location) => {
        try {
          await axios.put(global.config.url + `/locations/${location.id}`, locationPayload(location, { walletPassesEnabled }), {
            headers: { Authorization: `Bearer ${token}` },
          });
          return { id: location.id, success: true };
        } catch (error) {
          return { id: location.id, success: false, error };
        }
      }));
      const successfulIds = results.filter((result) => result.success).map((result) => result.id);
      const failedResults = results.filter((result) => !result.success);
      setLocations((current) => current.map((location) => successfulIds.indexOf(location.id) !== -1 ? { ...location, walletPassesEnabled } : location));
      if (failedResults.length > 0) {
        const accessFailure = failedResults.some((result) => handleAccessError(result.error, { deniedMessage: 'You do not have permission to update one or more locations.' }));
        if (!accessFailure && msgs.current) {
          msgs.current.show({ severity: 'error', summary: '', detail: `Wallet Passes could not be ${walletPassesEnabled ? 'enabled' : 'disabled'} for ${failedResults.length} location${failedResults.length === 1 ? '' : 's'}.` });
        }
      }
      if (successfulIds.length > 0 && msgs.current) {
        msgs.current.show({ severity: 'success', summary: '', detail: `Wallet Passes ${walletPassesEnabled ? 'enabled' : 'disabled'} for ${successfulIds.length} location${successfulIds.length === 1 ? '' : 's'}.` });
      }
    } finally {
      setLoading(false);
    }
  };

  const goBack = () => {
  
    //remove filters from local storage
    localStorage.removeItem('locationsFilters');
    navigate(-1);
  };

  const progressbar = (        
    <div className="card mt-3">
        <ProgressBar mode="indeterminate" style={{ height: '6px' }}></ProgressBar>
    </div>);

  if (accessIssue) {
    return <AccessState {...accessIssue} />;
  }

  return (
      <Container className='justify-content-md-center mt-10'>

        <div className="mt-5">

          <Container className="flex align-content-center flex-wrap">
            <Button link icon="pi pi-arrow-left" onClick={() => goBack()} className='flex mb-3'/> 
            <h4 className='flex mt-2 mb-3 ml-3'>Locations for customer '{customerName}'</h4> 
            <div>
              <span className="p-input-icon-left mb-3 ml-3">
                <i className="pi pi-search" />
                <InputText value={globalFilterValue} onChange={onGlobalFilterChange} placeholder="Filter by name" tooltip="Start typing a location name to filter" tooltipOptions={{position: "bottom"}} />
              </span>
              <Button icon="pi pi-times" className="p-button-text mb-3"  onClick={() => clearFilter()} tooltip="Clear filter" tooltipOptions={{position: "bottom"}} />
              <Button icon="pi pi-refresh" className="p-button-text mb-3" onClick={() => getLocations()} disabled={loading} tooltip="Refresh locations" tooltipOptions={{position: "bottom"}} />
              <Button icon="pi pi-plus" className="p-button-success mr-3 mb-3" rounded onClick={() => setCreateVisible(true)} disabled={loading} tooltip="Create new location" tooltipOptions={{position: "bottom"}} />
              {/* <Button icon="pi pi-trash" className="p-button-danger" rounded onClick={() => confirm(false)} disabled={loading} tooltip="Delete selected location" tooltipOptions={{position: "bottom"}} /> */}
              <Button type="button" icon="pi pi-file-export" className='mx-3 mb-3' rounded onClick={() => exportCSV(false)} disabled={loading} tooltip="Download as csv" tooltipOptions={{position: "bottom"}} />
            </div>
          </Container>
            
        </div>

        <Messages ref={msgs} />
        <Toast ref={toastBC} position="top-center" />
        <div className='justify-content-md-center mt-3'>
        {loading && progressbar}

          <DataTable value={locations} selectionMode="single" selection={rowSelectionModel} onSelectionChange={(e) => setRowSelectionModel(e.value)} 
            dataKey="id" tableStyle={{ minWidth: '5rem' }} filters={filters} stripedRows
            globalFilterFields={['name', 'extra']} emptyMessage={loading ? "Loading locations..." : "No locations found."} ref={dt}
            scrollable scrollHeight="80vh">
              {/* <Column selectionMode="single" headerStyle={{ width: '2rem' }}></Column> */}
              <Column field="options" header="" body={locationOptiosTemplate}> </Column>
              <Column field="edit" header="" body={editActionTemplate}></Column>
              <Column field="name" header="Name" showFilterMenu={false} filterMenuStyle={{ width: '14rem' }} 
              style={{ minWidth: '12rem' }} sortable></Column>
              <Column field="extra" header="Extra"></Column>
              <Column field="gympassVenueId" header="Wellhub Venue ID"></Column>
              <Column
                field="walletPassesEnabled"
                header={<span className="flex align-items-center gap-2"><Checkbox checked={areWalletPassesEnabledForAll} indeterminate={areWalletPassesEnabledForSome} onChange={(e) => updateWalletPassesForAllLocations(Boolean(e.checked))} aria-label="Enable or disable Wallet Passes for all locations" /><span>Wallet Passes</span></span>}
                body={walletPassesTemplate}
              ></Column>

          </DataTable>
          <OverlayPanel ref={op}>
            {startContent}
          </OverlayPanel>
        </div>

        <div className="card flex justify-content-center">
          <Dialog
            header="Edit Location"
            className='justify-content-center'
            visible={editVisible}
            onHide={() => closeEditDialog()}
            style={{ width: 'auto' }}
            breakpoints={{ '960px': '75vw', '641px': '100vw' }}
            footer={
              <div className='flex justify-content-center mb-2'>
                <Button label="Cancel" icon="pi pi-times" className="p-button-warning" onClick={() => closeEditDialog()} disabled={editSaving} />
                <Button label="Save" icon="pi pi-check" className="p-button-success ml-3" onClick={() => saveEditDialog()} loading={editSaving} disabled={!String(editForm.name || '').trim()} />
              </div>
            }
          >
            <Messages ref={editMsgs} />
            <Container className="flex align-content-center flex-wrap">
              <span className="flex p-float-label my-4">
                <InputText
                  id="edit-location-name"
                  value={editForm.name}
                  onChange={(e) => setEditForm((prev) => ({ ...prev, name: e.target.value }))}
                />
                <label htmlFor="edit-location-name">Name</label>
              </span>
              <span className="flex p-float-label my-4 ml-3">
                <InputText
                  id="edit-location-extra"
                  value={editForm.extra}
                  onChange={(e) => setEditForm((prev) => ({ ...prev, extra: e.target.value }))}
                />
                <label htmlFor="edit-location-extra">Extra</label>
              </span>
              <span className="flex p-float-label my-4 ml-3">
                <InputText
                  id="edit-location-gympass"
                  value={editForm.gympassVenueId}
                  onChange={(e) => setEditForm((prev) => ({ ...prev, gympassVenueId: e.target.value }))}
                />
                <label htmlFor="edit-location-gympass">Wellhub Venue ID</label>
              </span>
              <div className="flex align-items-center my-4 ml-3">
                <Checkbox
                  inputId="edit-location-wallet-passes"
                  checked={editForm.walletPassesEnabled}
                  onChange={(e) => setEditForm((prev) => ({ ...prev, walletPassesEnabled: Boolean(e.checked) }))}
                />
                <label htmlFor="edit-location-wallet-passes" className="ml-2 mb-0">Wallet Passes</label>
              </div>
              <span className="flex p-float-label my-4 ml-3">
                <InputText
                  id="edit-location-wallet-portal-code"
                  value={editForm.walletPassPortalCode}
                  onChange={(e) => setEditForm((prev) => ({ ...prev, walletPassPortalCode: e.target.value }))}
                />
                <label htmlFor="edit-location-wallet-portal-code">Wallet Pass Portal Code</label>
              </span>
            </Container>
          </Dialog>
        </div>

        <div className="card flex justify-content-center">
          <Dialog header="Create Location" className='justify-content-center' visible={createVisible} onHide={() => setCreateVisible(false)} 
            style={{ width: 'auto' }} breakpoints={{ '960px': '75vw', '641px': '100vw' }} footer={footerContent}>

            <Toast ref={toast} />
            <Container className="flex align-content-center flex-wrap">
              <span className="flex p-float-label my-4">

                <InputText
                    id="name"
                    name="name"
                    value={newLocationName}
                    onChange={(e) => setNewLocationName(e.target.value)}
                />
                <label htmlFor="input_name">Name</label>
              </span>
              <span className="flex p-float-label my-4 ml-3">
                <InputText
                    id="extra"
                    name="extra"
                    value={newLocationExtra}
                    onChange={(e) => setNewLocationExtra(e.target.value)}

                />
                <label htmlFor="input_extra">Extra</label>
              </span>
            </Container>
          </Dialog>
        </div>
        <ScrollTop />
      </Container>


  );
};

export default Locations;
